import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { fadeUp, staggerContainer, viewportOnce } from "../../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Technical Consulting",
  "LMS / Training Hub",
  "Digital Media Production",
  "Bosun's Mate",
  "Bell Health",
  "Tradewinds",
  "Tide Atlas",
  "General Inquiry",
];

const fieldClass =
  "w-full border-b border-[#0C2340]/15 bg-transparent px-0 py-3 font-body text-[#0C2340] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#0C2340]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad relative bg-[#DCE4EE]" data-testid="contact-section">
      <div className="hairline-light absolute left-0 right-0 top-0" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Left copy */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-5"
          >
            <motion.p variants={fadeUp} className="overline overline-dark mb-6">/ Consultation</motion.p>
            <motion.h2 variants={fadeUp} className="font-display text-5xl leading-tight tracking-tight text-[#0C2340] md:text-7xl">
              Let&rsquo;s
              <span> talk.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-6 max-w-md font-body text-lg leading-relaxed text-[#1E3A5F]">
              Tell us about your project, challenge, or idea. We&rsquo;ll
              respond with a clear plan for the digital infrastructure you
              need.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10 space-y-2">
              <p className="overline overline-dark">/ Reach the Studio</p>
              <a href="mailto:hello@tidewaterstudios.ca" className="block font-display text-2xl text-[#0C2340] transition-colors hover:text-[#1E3A5F]" data-testid="contact-email-link">
                hello@tidewaterstudios.ca
              </a>
              <p className="font-body text-sm text-[#2E4A68]">Whitehorse, Yukon Territory, Canada</p>
            </motion.div>
          </motion.div>

          {/* Form */}
          <motion.form
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            onSubmit={submit}
            className="card-light rounded-sm p-8 md:p-10 lg:col-span-6 lg:col-start-7"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <label className="overline overline-dark mb-2 block">Name *</label>
                <input value={form.name} onChange={update("name")} placeholder="Your name" className={fieldClass} data-testid="contact-input-name" />
              </div>
              <div>
                <label className="overline overline-dark mb-2 block">Email *</label>
                <input type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={fieldClass} data-testid="contact-input-email" />
              </div>
              <div>
                <label className="overline overline-dark mb-2 block">Company</label>
                <input value={form.company} onChange={update("company")} placeholder="Organization" className={fieldClass} data-testid="contact-input-company" />
              </div>
              <div>
                <label className="overline overline-dark mb-2 block">Interest</label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger
                    data-testid="contact-select-service"
                    className="w-full rounded-none border-0 border-b border-[#0C2340]/15 bg-transparent px-0 py-3 font-body text-[#0C2340] focus:ring-0 focus:border-[#0C2340] data-[placeholder]:text-[#94A3B8]"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="border-[#0C2340]/10 bg-white text-[#0C2340]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} className="font-body focus:bg-[#EAF0F7] focus:text-[#0C2340]" data-testid={`contact-service-option-${s.toLowerCase().replace(/[^a-z]/g, "")}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8">
              <label className="overline overline-dark mb-2 block">Message *</label>
              <textarea value={form.message} onChange={update("message")} rows={4} placeholder="Describe your project or challenge..." className={`${fieldClass} resize-none`} data-testid="contact-input-message" />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-ink font-mono-2 mt-10 flex w-full items-center justify-center gap-3 rounded-sm px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.18em] disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />}
              {loading ? "Transmitting..." : "Request Consultation"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
